/**
 * Media Helpers for LOOPINO AI Platform
 * Utility functions for consistent media URL handling across components
 */

/**
 * Get first media URL from a post (consistent logic across all components)
 * @param {Object} post - Post object
 * @returns {string|null} - First media URL or null if no media
 */
export const getFirstMediaUrl = (post) => {
  if (!post) return null;
  
  // Support both API format (media_urls) and legacy format (images)
  const mediaUrls = post?.media_urls || post?.images || [];
  if (mediaUrls.length > 0) {
    return mediaUrls[0];
  }
  
  // Fallback to legacy single image fields
  return post?.post_image_url || post?.image_url || null;
};

/**
 * Get all media URLs from a post
 * @param {Object} post - Post object
 * @returns {Array} - Array of media URLs
 */
export const getAllMediaUrls = (post) => {
  if (!post) return [];
  
  // Support both API format (media_urls) and legacy format (images)
  const mediaUrls = post?.media_urls || post?.images || [];
  if (mediaUrls.length > 0) {
    return mediaUrls;
  }
  
  // Fallback to legacy single image fields
  const singleUrl = post?.post_image_url || post?.image_url;
  return singleUrl ? [singleUrl] : [];
};

/**
 * Check if URL is a video file
 * @param {string} url - Media URL
 * @returns {boolean} - True if URL is a video file
 */
export const isVideoFile = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.ogg', '.mkv', '.m4v'];
  const urlLowerCase = url.toLowerCase();
  return videoExtensions.some(ext => urlLowerCase.includes(ext));
};

/**
 * Check if URL is an image file
 * @param {string} url - Media URL
 * @returns {boolean} - True if URL is an image file
 */
export const isImageFile = (url) => {
  if (!url) return false;
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
  const urlLowerCase = url.toLowerCase();
  return imageExtensions.some(ext => urlLowerCase.includes(ext));
};

/**
 * Get media type from URL
 * @param {string} url - Media URL
 * @returns {string} - 'video', 'image', or 'unknown'
 */
export const getMediaType = (url) => {
  if (isVideoFile(url)) return 'video';
  if (isImageFile(url)) return 'image';
  return 'unknown';
};